import { z } from 'zod';
import {
  GENDER_FIELD_NAMES,
  GENDER_OPTION_VALUES,
  GENDER_OTHER_TEXT_MAX_LENGTH,
  GENDER_OTHER_VALUE,
  GENDER_MESSAGES,
} from '../constants/genderOptions';

// Phone number entry schema matching PhoneEntryDto from API
export const phoneEntrySchema = z.object({
  phoneType: z.string().nullable().optional(),
  countryCode: z.string().nullable().optional(),
  phoneNumber: z.string().nullable().optional(),
  isPrimary: z.boolean().nullable().optional(),
  extension: z.string().nullable().optional(),
});

// Phone numbers schema matching PhoneNumbersDto from API (jsonb field)
export const phoneNumbersSchema = z.object({
  phones: z.array(phoneEntrySchema).nullable().optional(),
});

export const userProfileSchema = z.object({
  userId: z.number().int(),
  userName: z.string().nullable().optional(),
  firstName: z.string().nullable().optional(),
  middleName: z.string().nullable().optional(), // New field from API
  lastName: z.string().nullable().optional(),
  suffix: z.string().nullable().optional(),
  gender: z.string().nullable().optional(),
  otherGender: z.string().nullable().optional(),
  dateOfBirth: z.string().nullable().optional(),
  emailAddress: z.string().nullable().optional(),
  phone: z.string().nullable().optional(), // Legacy field - kept for backward compatibility
  phoneNumbers: phoneNumbersSchema.nullable().optional(), // New jsonb field
  addressLine1: z.string().nullable().optional(),
  addressLine2: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  zip: z.string().nullable().optional(),
  entraObjectId: z.string().nullable().optional(),
  createdDate: z.string().nullable().optional(),
  // API returns createdBy as string, but we accept both string and number for compatibility
  createdBy: z.union([z.string(), z.number()]).nullable().optional(),
  updatedDate: z.string().nullable().optional(),
  // API returns updatedBy as string, but we accept both string and number for compatibility
  updatedBy: z.union([z.string(), z.number()]).nullable().optional(),
  // New fields from API (optional, won't break existing code)
  roles: z.array(z.any()).nullable().optional(),
  clinicianProfile: z.any().nullable().optional(),
  examineeProfile: z.any().nullable().optional(),
});

export const userProfileResponseSchema = z.object({
  success: z.boolean(),
  data: userProfileSchema.nullable().optional(),
  message: z.string().nullable().optional(),
  errors: z.array(z.string()).nullable().optional(),
});

export const updateUserProfileRequestSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  middleName: z.string().nullable(), // Always include middleName (can be string or null to clear)
  suffix: z.string().nullable(), // Always include suffix (can be string or null to clear)
  gender: z.enum(GENDER_OPTION_VALUES, { required_error: GENDER_MESSAGES.REQUIRED }),
  otherGender: z.string().max(GENDER_OTHER_TEXT_MAX_LENGTH).nullable().optional(),
  dateOfBirth: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date of birth must be in YYYY-MM-DD format'),
  phone: z.string().min(10, 'Phone number must be at least 10 digits').nullable().optional(), // Legacy field - optional now (using phoneNumbers instead)
  phoneNumbers: phoneNumbersSchema.nullable().optional(), // New jsonb field - optional but should be provided when phone is not provided
  addressLine1: z.string().min(1, 'Address line 1 is required'),
  addressLine2: z.string().nullable(), // Always include addressLine2 (can be string or null to clear)
  city: z.string().min(1, 'City is required'),
  state: z.string().length(2, 'State must be 2 characters'),
  zip: z.string().min(5, 'ZIP code must be at least 5 characters'),
  // API accepts updatedBy as string, but we accept both string and number for compatibility
  updatedBy: z.union([z.string(), z.number()]).nullable().optional(),
  // Optional new fields from API (not currently used by frontend)
  userName: z.string().nullable().optional(),
  emailAddress: z.string().nullable().optional(),
  entraObjectId: z.string().uuid().nullable().optional(),
})
  .refine(
    (data) => {
      // Either phone (legacy) or phoneNumbers (new) must be provided
      return data.phone || (data.phoneNumbers && data.phoneNumbers.phones && data.phoneNumbers.phones.length > 0);
    },
    {
      message: 'Either phone or phoneNumbers must be provided',
      path: ['phoneNumbers'], // Show error on phoneNumbers field
    }
  )
  .refine(
    (data) => data.gender !== GENDER_OTHER_VALUE || Boolean(data.otherGender?.trim()),
    {
      message: GENDER_MESSAGES.SPECIFY_WHEN_OTHER_ZOD,
      path: [GENDER_FIELD_NAMES.OTHER_GENDER],
    }
  );

export type UserProfile = z.infer<typeof userProfileSchema>;
export type UpdateUserProfileRequest = z.infer<typeof updateUserProfileRequestSchema>;

